import { useCallback, useEffect, useRef, useState } from 'react';
import {
  getAnthropicKeyStatus,
  listManagedAgents,
} from '../managed-agents/api';
import type {
  ManagedAgentList,
  WorkspaceKeyStatus,
} from '../managed-agents/types';

/**
 * The workspace's Claude Console connection: whether a key is saved, and the
 * agents and environments that key can see.
 */
export function useClaudeConsole(workspaceId: string | undefined) {
  const [keyStatus, setKeyStatus] = useState<WorkspaceKeyStatus | null>(null);
  const [remote, setRemote] = useState<ManagedAgentList | null>(null);
  const [error, setError] = useState<string | null>(null);

  // The workspace answers are for. Every load checks it before applying, so
  // nothing from a previous workspace lands after a switch, including loads
  // started by hand rather than by an effect.
  const activeWorkspace = useRef(workspaceId);
  activeWorkspace.current = workspaceId;

  useEffect(() => {
    setKeyStatus(null);
    setRemote(null);
    if (!workspaceId) return;
    setError(null);
    getAnthropicKeyStatus(workspaceId)
      .then((status) => {
        if (activeWorkspace.current === workspaceId) setKeyStatus(status);
      })
      .catch((err) => {
        if (activeWorkspace.current === workspaceId) setError(err.message);
      });
  }, [workspaceId]);

  // Only the latest agent list counts: one requested before the key was
  // replaced, or before another reload, lists what that key could see.
  const latestRemoteRequest = useRef(0);

  const reloadRemote = useCallback(() => {
    const request = ++latestRemoteRequest.current;
    if (!workspaceId || !keyStatus?.connected) {
      setRemote(null);
      return;
    }
    const isLatest = () =>
      latestRemoteRequest.current === request &&
      activeWorkspace.current === workspaceId;
    setError(null);
    listManagedAgents(workspaceId)
      .then((list) => {
        if (isLatest()) setRemote(list);
      })
      .catch((err) => {
        if (isLatest()) setError(err.message);
      });
    // Keyed on the status itself, not the displayed hint: every key save
    // returns a new status, even when the new key ends in the same characters.
  }, [workspaceId, keyStatus]);

  useEffect(reloadRemote, [reloadRemote]);

  return { keyStatus, setKeyStatus, remote, error, reloadRemote };
}
